import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { CsrStatusSelect } from "../../../components/CsrStatusSelect";
import { VipTagSelect } from "../../../components/VipTagSelect";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useVipTags } from "../../../hooks/useVipTags";
import { Aircraft, CsrStatus, Tenant, TenantType } from "../../../types";
import { SCHEDULE_MAX_DATE, SCHEDULE_MIN_DATE } from "../../../utils/dateConstants";
import { isInfinityDate, INFINITY_DATE_STRING } from "../../../utils/dateHelpers";
import { SelectTailNumber } from "../../../widgets/SelectTailNumber";
import { AutocompleteAircraft } from "../../Settings/HangarSettings/AutocompleteAircraft";

interface CreateTripDialogPresenterProps {
  // Dialog state
  open: boolean;
  onClose: () => void;

  // Form data
  formData: {
    tailNumber: string;
    ownerManager: string;
    tenantType: string;
    vipTagIds?: string[];
    selectedAircraft: Aircraft | null;
    selectedTenant: Tenant | null;
    arrivalDate: Date | null;
    departureDate: Date | string | null; // Can be Date or "infinity" string
    notes: string;
    csrStatus: CsrStatus;
  };

  // Form handlers
  setFormData: (data: any) => void;
  handleTenantChange: (tenant: Tenant) => void;
  handleTailNumberChange: (tailNumber: string) => void;
  handleSubmit: () => void;

  // Validation
  validationErrors: {
    tailNumber?: string;
    aircraft?: string;
    arrivalDate?: string;
    departureDate?: string;
  };
  isFormValid: boolean;

  // Loading states
  loading: boolean;
  checkingConflicts: boolean;

  // Errors and warnings
  error: string | null;
  setError: (error: string | null) => void;
  conflictWarning: string | null;
  setConflictWarning: (warning: string | null) => void;

  // Available tenants
  tenants: Tenant[];

  // Location info
  currentLocationId?: string;
  currentLocationType?: "hangar" | "ramp";

  // Simple mode
  simpleMode?: boolean;
  existingTenant?: any; // TripWithTenant type
}

export const CreateTripDialogPresenter: React.FC<
  CreateTripDialogPresenterProps
> = ({
  open,
  onClose,
  formData,
  setFormData,
  handleTenantChange,
  handleTailNumberChange,
  handleSubmit,
  validationErrors,
  isFormValid,
  loading,
  checkingConflicts,
  error,
  setError,
  conflictWarning,
  setConflictWarning,
  tenants,
  currentLocationId,
  currentLocationType,
  simpleMode = false,
  existingTenant,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { vipTags } = useVipTags(activeFBO?.id);

  // Track whether "No scheduled departure" checkbox is checked
  const [noScheduledDeparture, setNoScheduledDeparture] = React.useState(false);

  // Sync checkbox state with formData.departureDate
  React.useEffect(() => {
    setNoScheduledDeparture(isInfinityDate(formData.departureDate));
  }, [formData.departureDate]);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="create-trip-dialog-title"
        PaperProps={{
          sx: {
            // iPad optimization
            maxWidth: { xs: "100%", sm: "500px", md: "600px" },
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: "100vh", sm: "85vh" },
            zIndex: 9600, // Higher than TripDetailsModal (9500)
          },
        }}
        sx={{
          zIndex: 9600, // Ensure backdrop is also above TripDetailsModal
        }}
      >
        <DialogTitle id="create-trip-dialog-title">
          {simpleMode && existingTenant
            ? t('screens.schedule.createTripDialog.title.newTripFor', { tailNumber: existingTenant.tail_number })
            : t('screens.schedule.createTripDialog.title.create')}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Error display */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Conflict warning */}
            {conflictWarning && (
              <Alert
                severity="warning"
                onClose={() => setConflictWarning(null)}
                action={
                  checkingConflicts ? <CircularProgress size={20} /> : undefined
                }
              >
                {conflictWarning}
              </Alert>
            )}

            {/* Show aircraft info in simple mode, or full form fields in normal mode */}
            {simpleMode && existingTenant ? (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>{existingTenant.tail_number}</strong>
                  {existingTenant.owner_manager &&
                    ` • ${existingTenant.owner_manager}`}
                  {existingTenant.aircraft_model &&
                    ` • ${existingTenant.aircraft_model}`}
                </Typography>
              </Alert>
            ) : (
              <>
                {/* Tail Number Selection */}
                <SelectTailNumber
                  key={open ? "dialog-open" : "dialog-closed"}
                  disableAutocomplete={false}
                  defaultValue=""
                  tenants={tenants}
                  onInputChange={handleTailNumberChange}
                  onChange={handleTenantChange}
                  warning={validationErrors.tailNumber}
                />

                <Stack direction="row" spacing={2}>
                  {/* Owner/Manager */}
                  <TextField
                    label={t('screens.schedule.createTripDialog.labels.ownerManager')}
                    value={formData.ownerManager}
                    onChange={(e) =>
                      setFormData({ ownerManager: e.target.value })
                    }
                    fullWidth
                    size="small"
                    placeholder={t('screens.schedule.createTripDialog.placeholders.ownerManager')}
                  />

                  {/* Tenant Type */}
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>{t("screens.schedule.createTripDialog.labels.type", "Type")}</InputLabel>
                    <Select
                      value={formData.tenantType}
                      label={t("screens.schedule.createTripDialog.labels.type", "Type")}
                      onChange={(e) => setFormData({ tenantType: e.target.value })}
                      MenuProps={{ sx: { zIndex: 10000 } }}
                    >
                      <MenuItem value={TenantType.TRANSIENT}>
                        {t("screens.schedule.createTripDialog.tenantTypes.transient", "Transient")}
                      </MenuItem>
                      <MenuItem value={TenantType.BASE}>
                        {t("screens.schedule.createTripDialog.tenantTypes.base", "Base")}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* VIP Tags */}
                  <VipTagSelect
                    value={formData.vipTagIds || []}
                    availableTags={vipTags}
                    onChange={(tagIds) => setFormData({ vipTagIds: tagIds })}
                  />

                  {/* CSR Status */}
                  <CsrStatusSelect
                    value={formData.csrStatus}
                    onChange={(newStatus) => setFormData({ csrStatus: newStatus })}
                    selectProps={{
                      MenuProps: { sx: { zIndex: 10000 } },
                    }}
                  />
                </Stack>

                {/* Aircraft Selection */}
                <Stack spacing={1}>
                  <AutocompleteAircraft
                    key={formData.selectedAircraft?.id || "no-aircraft"}
                    label={t('screens.schedule.createTripDialog.labels.searchAircraft')}
                    onSelect={(aircraft) =>
                      setFormData({ selectedAircraft: aircraft })
                    }
                    defaultSelected={formData.selectedAircraft}
                  />
                  {validationErrors.aircraft && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {validationErrors.aircraft}
                    </Typography>
                  )}
                </Stack>
              </>
            )}

            {/* Arrival Date */}
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t('screens.schedule.createTripDialog.labels.arrivalDateTime')} <span style={{ color: 'red' }}>{t('screens.schedule.createTripDialog.labels.required')}</span>
              </Typography>
              <DateTimePicker
                label=""
                value={formData.arrivalDate}
                onChange={(newValue) => setFormData({ arrivalDate: newValue })}
                minDate={SCHEDULE_MIN_DATE}
                ampm={false}
                minutesStep={15}
                maxDate={SCHEDULE_MAX_DATE}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    size: "small",
                    error: !!validationErrors.arrivalDate,
                    helperText: validationErrors.arrivalDate,
                  },
                  popper: {
                    sx: {
                      zIndex: 10000, // Higher than dialog (9600)
                    },
                  },
                }}
              />
            </Stack>

            {/* Departure Date */}
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {t('screens.schedule.createTripDialog.labels.departureDateTime')} {!noScheduledDeparture && <span style={{ color: 'red' }}>{t('screens.schedule.createTripDialog.labels.required')}</span>}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={noScheduledDeparture}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNoScheduledDeparture(checked);
                        if (checked) {
                          // Set departure to infinity
                          setFormData({ departureDate: INFINITY_DATE_STRING });
                        } else {
                          // Reset to default (2 days from arrival)
                          const defaultDeparture = formData.arrivalDate
                            ? addDays(formData.arrivalDate, 2)
                            : addDays(new Date(), 2);
                          setFormData({ departureDate: defaultDeparture });
                        }
                      }}
                      disabled={loading}
                    />
                  }
                  label={t('screens.schedule.createTripDialog.labels.noDeparture')}
                />
              </Stack>

              {!noScheduledDeparture ? (
                <DateTimePicker
                  label=""
                  value={formData.departureDate instanceof Date ? formData.departureDate : null}
                  onChange={(newValue) => setFormData({ departureDate: newValue })}
                  minDate={
                    formData.arrivalDate && formData.arrivalDate > SCHEDULE_MIN_DATE
                      ? formData.arrivalDate
                      : SCHEDULE_MIN_DATE
                  }
                  ampm={false}
                  minutesStep={15}
                  minDateTime={formData.arrivalDate || undefined}
                  maxDate={SCHEDULE_MAX_DATE}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: "small",
                      error: !!validationErrors.departureDate,
                      helperText: validationErrors.departureDate,
                    },
                    popper: {
                      sx: {
                        zIndex: 10000, // Higher than dialog (9600)
                      },
                    },
                  }}
                />
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('screens.schedule.createTripDialog.messages.noScheduledDeparture')}
                </Alert>
              )}
            </Stack>

            {/* Notes */}
            <TextField
              label={t('screens.schedule.createTripDialog.labels.notes')}
              value={formData.notes}
              onChange={(e) => setFormData({ notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
              size="small"
              placeholder={t('screens.schedule.createTripDialog.placeholders.notes')}
            />

            {/* Info box - show different message for simple mode */}
            {simpleMode ? (
              currentLocationId ? (
                <Alert severity="success">
                  {t('screens.schedule.createTripDialog.messages.autoAssigned', { locationType: currentLocationType })}
                </Alert>
              ) : (
                <Alert severity="info">
                  {t('screens.schedule.createTripDialog.messages.autoAssignedSimple')}
                </Alert>
              )
            ) : (
              <>
                {!currentLocationId && (
                  <Alert severity="info">
                    {t('screens.schedule.createTripDialog.messages.unassigned')}
                  </Alert>
                )}
                {currentLocationId && (
                  <Alert severity="success">
                    {t('screens.schedule.createTripDialog.messages.autoAssigned', { locationType: currentLocationType })}
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            {t('screens.schedule.createTripDialog.buttons.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !isFormValid || !!conflictWarning}
            title={
              conflictWarning
                ? t('screens.schedule.createTripDialog.tooltips.conflict')
                : !isFormValid
                ? t('screens.schedule.createTripDialog.tooltips.fillRequired')
                : ""
            }
          >
            {loading ? <CircularProgress size={24} /> : t('screens.schedule.createTripDialog.buttons.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};
